import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button, Select, MenuItem, InputLabel, FormControl, Stack,
  Box, Typography, Alert, Chip, Collapse,
  CircularProgress, ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel, Slider,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import OutputIcon from '@mui/icons-material/Output';
import CodeIcon from '@mui/icons-material/Code';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import ShieldIcon from '@mui/icons-material/Shield';
import TuneIcon from '@mui/icons-material/Tune';

import { PLATFORM_PRESETS } from '../../constants/platformPresets';
import { ConsoleDialogFrame } from '../console/ConsoleDialogFrame';

const INITIAL_STATE = {
  platformDistributor: 'facebook',
  regionLocation: '',
  protocol: 'rtmp',
  streamKey: '',
  destination: '',
  streamId: '',
  passphrase: '',
  secret: '',
  videoCodec: 'copy',
  audioCodec: 'copy',
  videoBitrate: 'copy',
  audioBitrate: 'copy',
  resolution: 'copy',
  aspectRatio: 'copy',
  frameRate: 'copy',
  keyframeInterval: 'copy',
  bitrateMode: 'cbr',
  crfValue: null,
  transcodePreset: 'medium',
  transcodeProfile: 'high',
  transcodeLevel: '4.1',
  audioSampleRate: '48000',
  audioChannels: '2',
  deinterlace: 'none',
  scaleFilter: 'copy',
  loudnorm: false,
  loudnormTarget: '-23',
  logoOverlayEnabled: false,
  logoOverlayPosition: 'top-left',
  logoOverlayOpacity: 100,
  deliveryMode: 'PUSH',
  pullLabel: '',
  pullProtocol: 'SRT',
  pullPort: null,
  pullKey: '',
  pullSecret: '',
  pullBufferMs: 250,
  pullLatencyMs: 250000,
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  initialData?: any;
  userRole: string;
  mode?: 'DIRECT' | 'MANAGED' | 'SERVICE' | 'SOCIAL' | null;
}

const SegmentedControl = ({ value, options, onChange, disabled = false }: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => (
  <ToggleButtonGroup
    exclusive
    fullWidth
    size="small"
    value={value}
    onChange={(_event, next) => next && onChange(next)}
    disabled={disabled}
    sx={(theme) => ({
      bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.04),
      borderRadius: 2,
      p: 0.375,
      gap: 0.375,
      '& .MuiToggleButton-root': {
        borderRadius: 1.5,
        border: 0,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.8rem',
        py: 0.625,
        px: 1,
        color: 'text.secondary',
        transition: 'background-color 120ms ease, color 120ms ease',
        '&.Mui-selected': {
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 1px 2px rgba(0,0,0,0.4)'
            : '0 1px 2px rgba(15,23,42,0.12)',
          '&:hover': { bgcolor: 'background.paper' },
        },
        '&:hover': {
          bgcolor: alpha(theme.palette.text.primary, 0.04),
        },
      },
    })}
  >
    {options.map((option) => (
      <ToggleButton key={option.value} value={option.value}>
        {option.label}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

const SectionPanel = ({ icon, title, meta, children, subdued = false }: {
  icon?: React.ReactNode;
  title: string;
  meta?: React.ReactNode;
  children: React.ReactNode;
  subdued?: boolean;
}) => (
  <Box
    sx={(theme) => ({
      border: '1px solid',
      borderColor: theme.palette.divider,
      borderRadius: 3,
      bgcolor: subdued
        ? alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.025 : 0.018)
        : theme.palette.background.paper,
      overflow: 'hidden',
    })}
  >
    <Box
      sx={{
        px: 2,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        {icon ? <Box sx={{ display: 'inline-flex', color: 'text.secondary' }}>{icon}</Box> : null}
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.01em', color: 'text.primary' }} noWrap>
          {title}
        </Typography>
      </Stack>
      {meta}
    </Box>
    <Box sx={{ p: 2 }}>
      {children}
    </Box>
  </Box>
);

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.625, color: 'text.secondary', fontSize: '0.72rem' }}>
    {children}
  </Typography>
);

const ControlPair = ({ left, right }: { left: React.ReactNode; right: React.ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
      gap: { xs: 1.5, sm: 2 },
      alignItems: 'start',
    }}
  >
    <Box sx={{ minWidth: 0 }}>{left}</Box>
    <Box sx={{ minWidth: 0 }}>{right}</Box>
  </Box>
);

const AdvancedOptions = ({ formData, handleInputChange, canTranscode, isManagedMode, isSocialMode }: any) => {
  const [expanded, setExpanded] = useState(isManagedMode);
  const disabled = isSocialMode || !canTranscode;

  const setField = (name: string, value: string) => {
    handleInputChange({ target: { name, value } });
  };

  const renderSegmented = (name: string, label: string, options: Array<{ value: string; label: string }>) => (
    <Box sx={{ minWidth: 0 }}>
      <FieldLabel>{label}</FieldLabel>
      <SegmentedControl
        value={formData[name]}
        options={options}
        onChange={(value) => setField(name, value)}
        disabled={disabled}
      />
    </Box>
  );

  const renderSelect = (name: string, label: string, options: any[]) => (
    <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
      <InputLabel>{label}</InputLabel>
      <Select name={name} value={formData[name]} label={label} onChange={handleInputChange} disabled={isSocialMode}>
        {options.map(opt => (
          <MenuItem
            key={opt.value}
            value={opt.value}
            disabled={
              isSocialMode ||
              (opt.value !== 'copy' && !canTranscode) ||
              (opt.value === 'copy' && isManagedMode && (name === 'videoCodec' || name === 'audioCodec'))
            }
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            {opt.label}
            {opt.value !== 'copy' && !canTranscode && <Chip label="PRO" size="small" icon={<LockIcon />} sx={{ height: 18, fontSize: '0.62rem', borderRadius: 1 }} />}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
      <Box
        onClick={() => !isSocialMode && setExpanded(!expanded)}
        sx={(theme) => ({
          cursor: isSocialMode ? 'default' : 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.25,
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.05),
          transition: 'background-color 120ms ease',
          '&:hover': isSocialMode ? {} : {
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
          },
        })}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CodeIcon color={canTranscode ? 'primary' : 'action'} fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>Processing</Typography>
          <Chip
            size="small"
            label={canTranscode ? 'Remapped' : 'Locked'}
            color={canTranscode ? 'primary' : 'default'}
            variant={canTranscode ? 'filled' : 'outlined'}
            sx={{ borderRadius: 1.5, height: 22, fontSize: '0.68rem', fontWeight: 600 }}
          />
        </Box>
        {!isSocialMode && (expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />)}
      </Box>
      <Collapse in={isSocialMode ? true : expanded}>
        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
          {!isSocialMode && !canTranscode && (
            <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.8rem', borderRadius: 2 }}>Advanced settings are locked for Direct Output. Use Remapped Output to unlock.</Alert>
          )}

          {!isSocialMode && (
            <Stack spacing={1.5} sx={{ opacity: canTranscode ? 1 : 0.62 }}>
              <SectionPanel title="Video Path" icon={<TuneIcon fontSize="small" />}>
                <Stack spacing={1.5}>
                  <ControlPair
                    left={renderSegmented('videoCodec', 'Video Codec', [
                      { value: 'copy', label: 'Copy' },
                      { value: 'libsvtav1', label: 'AV1' },
                      { value: 'libvpx-vp9', label: 'VP9' },
                      { value: 'libx264', label: 'H264' },
                      { value: 'libx265', label: 'H265' },
                    ])}
                    right={renderSegmented('bitrateMode', 'Rate Control', [
                      { value: 'copy', label: 'Copy' },
                      { value: 'cbr', label: 'CBR' },
                      { value: 'vbr', label: 'VBR' },
                    ])}
                  />
                  <ControlPair
                    left={renderSegmented('videoBitrate', 'Video Bitrate', [
                      { value: 'copy', label: 'Copy' },
                      { value: '1000k', label: '1' },
                      { value: '1500k', label: '1.5' },
                      { value: '2500k', label: '2.5' },
                      { value: '4000k', label: '4' },
                    ])}
                    right={renderSegmented('transcodePreset', 'Encoder Speed', [
                      { value: 'ultrafast', label: 'Ultra' },
                      { value: 'veryfast', label: 'Fast' },
                      { value: 'medium', label: 'Med' },
                      { value: 'slow', label: 'Slow' },
                    ])}
                  />
                  <ControlPair
                    left={renderSegmented('resolution', 'Resolution', [
                      { value: 'copy', label: 'Copy' },
                      { value: '2160', label: '2160p' },
                      { value: '1440', label: '1440p' },
                      { value: '1080', label: '1080p' },
                      { value: '720', label: '720p' },
                    ])}
                    right={renderSegmented('frameRate', 'Frame Rate', [
                      { value: 'copy', label: 'Copy' },
                      { value: '24', label: '24' },
                      { value: '25', label: '25' },
                      { value: '30', label: '30' },
                      { value: '50', label: '50' },
                      { value: '60', label: '60' },
                    ])}
                  />
                  <ControlPair
                    left={renderSegmented('keyframeInterval', 'Keyframe', [
                      { value: 'copy', label: 'Copy' },
                      { value: '1', label: '1s' },
                      { value: '2', label: '2s' },
                      { value: '5', label: '5s' },
                    ])}
                    right={renderSegmented('scaleFilter', 'Scaler', [
                      { value: 'copy', label: 'Off' },
                      { value: 'bilinear', label: 'Linear' },
                      { value: 'bicubic', label: 'Cubic' },
                      { value: 'lanczos', label: 'Lanczos' },
                    ])}
                  />
                  <ControlPair
                    left={renderSegmented('aspectRatio', 'Aspect Ratio', [
                      { value: 'copy', label: 'Copy' },
                      { value: '16:9', label: '16:9' },
                      { value: '4:3', label: '4:3' },
                      { value: '1:1', label: '1:1' },
                      { value: '9:16', label: '9:16' },
                      { value: '2.35:1', label: '2.35:1' },
                    ])}
                    right={renderSegmented('deinterlace', 'Deinterlace', [
                      { value: 'none', label: 'Off' },
                      { value: 'yadif', label: 'Yadif' },
                      { value: 'yadif2x', label: '2x' },
                      { value: 'bwdif', label: 'Bwdif' },
                    ])}
                  />
                  {formData.videoCodec === 'libx264' && (
                    <ControlPair
                      left={renderSelect('transcodeProfile', 'H264 Profile', [
                        { value: 'baseline', label: 'Baseline' },
                        { value: 'main', label: 'Main' },
                        { value: 'high', label: 'High' },
                      ])}
                      right={renderSelect('transcodeLevel', 'H264 Level', [
                        { value: '3.1', label: '3.1' },
                        { value: '4.0', label: '4.0' },
                        { value: '4.1', label: '4.1' },
                        { value: '5.1', label: '5.1' },
                      ])}
                    />
                  )}
                  <SectionPanel title="Logo Overlay" subdued>
                    <Stack spacing={1.25}>
                      <FormControlLabel
                        control={<Checkbox name="logoOverlayEnabled" checked={!!formData.logoOverlayEnabled} onChange={handleInputChange} disabled={disabled} />}
                        label="Enable logo overlay"
                      />
                      {formData.logoOverlayEnabled && (
                        <ControlPair
                          left={renderSegmented('logoOverlayPosition', 'Location', [
                            { value: 'top-left', label: 'Top Left' },
                            { value: 'top-right', label: 'Top Right' },
                          ])}
                          right={(
                            <Box sx={{ minWidth: 0 }}>
                              <FieldLabel>Opacity</FieldLabel>
                              <Slider
                                name="logoOverlayOpacity"
                                min={0}
                                max={100}
                                step={5}
                                value={Number(formData.logoOverlayOpacity ?? 100)}
                                valueLabelDisplay="auto"
                                disabled={disabled}
                                onChange={(_event, value) => handleInputChange({ target: { name: 'logoOverlayOpacity', value } })}
                              />
                            </Box>
                          )}
                        />
                      )}
                    </Stack>
                  </SectionPanel>
                </Stack>
              </SectionPanel>

              <SectionPanel title="Audio Path" icon={<TuneIcon fontSize="small" />}>
                <Stack spacing={1.5}>
                  <ControlPair
                    left={renderSegmented('audioCodec', 'Audio Codec', [
                      { value: 'copy', label: 'Copy' },
                      { value: 'aac', label: 'AAC' },
                      { value: 'libmp3lame', label: 'MP3' },
                      { value: 'libopus', label: 'Opus' },
                      { value: 'mp2', label: 'MP2' },
                    ])}
                    right={renderSegmented('audioBitrate', 'Audio Bitrate', [
                      { value: 'copy', label: 'Copy' },
                      { value: '128k', label: '128k' },
                      { value: '192k', label: '192k' },
                      { value: '256k', label: '256k' },
                      { value: '320k', label: '320k' },
                    ])}
                  />
                  <ControlPair
                    left={renderSegmented('audioSampleRate', 'Sample Rate', [
                      { value: 'copy', label: 'Copy' },
                      { value: '44100', label: '44.1k' },
                      { value: '48000', label: '48k' },
                    ])}
                    right={renderSegmented('audioChannels', 'Channels', [
                      { value: 'copy', label: 'Copy' },
                      { value: '1', label: 'Mono' },
                      { value: '2', label: 'Stereo' },
                    ])}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <FormControlLabel
                      control={<Checkbox name="loudnorm" checked={!!formData.loudnorm} onChange={handleInputChange} disabled={disabled} />}
                      label="Loudness normalization"
                    />
                    {formData.loudnorm && renderSelect('loudnormTarget', 'Target LUFS', [
                      { value: '-16', label: '-16 LUFS - Streaming' },
                      { value: '-23', label: '-23 LUFS - EBU R128' },
                      { value: '-24', label: '-24 LUFS - ATSC A/85' },
                    ])}
                  </Stack>
                </Stack>
              </SectionPanel>
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export const DestinationModal: React.FC<Props> = ({ open, onClose, onSave, initialData, userRole, mode }) => {
  const theme = useTheme();
  const [form, setForm] = useState(INITIAL_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSocial = mode === 'SOCIAL' || initialData?.isSocialStream === true;
  const isManagedService = mode === 'SERVICE' || initialData?.isManagedService === true;
  const isManaged = !isSocial && (
    mode === 'MANAGED' ||
    (initialData && ((initialData.videoCodec || 'copy') !== 'copy' || (initialData.audioCodec || 'copy') !== 'copy'))
  );
  const canTranscode = isManaged;
  const canSwitchDeliveryMode = !isSocial && !isManaged && !isManagedService;
  const isPullMode = canSwitchDeliveryMode && form.deliveryMode === 'PULL';
  const platformOptions = isSocial ? PLATFORM_PRESETS.filter((p) => p.id !== 'custom') : PLATFORM_PRESETS;

  useEffect(() => {
    if (open) {
      let key = '';
      let distributor = isSocial ? 'facebook' : 'facebook';

      if (initialData) {
        const preset = PLATFORM_PRESETS.find(p => p.name === initialData.platformDistributor) || PLATFORM_PRESETS.find(p => p.id === 'custom');
        if (preset && preset.id !== 'custom') {
          key = initialData.destination.replace(preset.urlHint, '').replace(/^\/+/, '');
        } else {
          key = initialData.destination;
        }
        distributor = preset?.id || (isSocial ? 'facebook' : 'custom');
      }

      const baseForm = {
        ...INITIAL_STATE,
        ...initialData,
        streamKey: key,
        platformDistributor: distributor,
        deliveryMode: initialData?.deliveryMode || (initialData?.isPullLink ? 'PULL' : 'PUSH'),
        pullLabel: initialData?.pullLabel || initialData?.platformDistributor || 'Pull',
        pullProtocol: initialData?.pullProtocol || (initialData?.protocol === 'rist' ? 'RIST' : 'SRT'),
        pullBufferMs: initialData?.pullBufferMs || 250,
        pullLatencyMs: initialData?.pullLatencyMs || 250000,
      };

      baseForm.bitrateMode = baseForm.bitrateMode === 'crf' ? 'cbr' : (baseForm.bitrateMode || (baseForm.videoCodec !== 'copy' ? 'cbr' : 'copy'));
      baseForm.crfValue = null;
      baseForm.transcodePreset = baseForm.transcodePreset || 'medium';
      baseForm.transcodeProfile = baseForm.transcodeProfile || 'high';
      baseForm.transcodeLevel = baseForm.transcodeLevel || '4.1';
      baseForm.audioSampleRate = baseForm.audioSampleRate || (baseForm.audioCodec !== 'copy' ? '48000' : 'copy');
      baseForm.audioChannels = baseForm.audioChannels || (baseForm.audioCodec !== 'copy' ? '2' : 'copy');
      baseForm.deinterlace = baseForm.deinterlace || 'none';
      baseForm.scaleFilter = baseForm.scaleFilter || 'copy';
      baseForm.loudnorm = baseForm.loudnorm === true;
      baseForm.loudnormTarget = baseForm.loudnormTarget || '-23';
      baseForm.logoOverlayEnabled = baseForm.logoOverlayEnabled === true;
      baseForm.logoOverlayPosition = baseForm.logoOverlayPosition || 'top-left';
      baseForm.logoOverlayOpacity = Number(baseForm.logoOverlayOpacity ?? 100);

      if (!initialData && mode === 'MANAGED') {
        baseForm.videoCodec = 'libx264';
        baseForm.audioCodec = 'aac';
        baseForm.videoBitrate = '4000k';
        baseForm.audioBitrate = '128k';
        baseForm.resolution = '720';
        baseForm.frameRate = '25';
        baseForm.keyframeInterval = '2';
        baseForm.bitrateMode = 'cbr';
        baseForm.crfValue = null;
        baseForm.transcodePreset = 'medium';
        baseForm.transcodeProfile = 'high';
        baseForm.transcodeLevel = '4.1';
        baseForm.audioSampleRate = '48000';
        baseForm.audioChannels = '2';
        baseForm.deinterlace = 'none';
        baseForm.scaleFilter = 'copy';
        baseForm.loudnorm = false;
        baseForm.loudnormTarget = '-23';
        baseForm.logoOverlayEnabled = false;
        baseForm.logoOverlayPosition = 'top-left';
        baseForm.logoOverlayOpacity = 100;
      }

      if (isSocial) {
        baseForm.protocol = 'rtmp';
        baseForm.deliveryMode = 'PUSH';
        baseForm.videoCodec = 'copy';
        baseForm.audioCodec = 'copy';
        baseForm.videoBitrate = 'copy';
        baseForm.audioBitrate = 'copy';
        baseForm.resolution = 'copy';
        baseForm.aspectRatio = 'copy';
        baseForm.frameRate = 'copy';
        baseForm.keyframeInterval = 'copy';
        baseForm.bitrateMode = 'copy';
        baseForm.audioSampleRate = 'copy';
        baseForm.audioChannels = 'copy';
        baseForm.deinterlace = 'none';
        baseForm.loudnorm = false;
        baseForm.logoOverlayEnabled = false;
        if (baseForm.platformDistributor === 'custom') baseForm.platformDistributor = 'facebook';
      }

      if (mode === 'MANAGED') {
        baseForm.deliveryMode = 'PUSH';
      }

      if (isManagedService) {
        baseForm.deliveryMode = 'PUSH';
        baseForm.videoCodec = 'copy';
        baseForm.audioCodec = 'copy';
        baseForm.videoBitrate = 'copy';
        baseForm.audioBitrate = 'copy';
        baseForm.resolution = 'copy';
        baseForm.aspectRatio = 'copy';
        baseForm.frameRate = 'copy';
        baseForm.keyframeInterval = 'copy';
        baseForm.bitrateMode = 'copy';
        baseForm.audioSampleRate = 'copy';
        baseForm.audioChannels = 'copy';
        baseForm.deinterlace = 'none';
        baseForm.loudnorm = false;
        baseForm.logoOverlayEnabled = false;
      }

      setForm(baseForm);
    }
  }, [open, initialData, mode, isSocial, isManagedService]);

  const updateFormValue = (name: string, value: any) => {
    if (isSocial && name === 'protocol') return;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'deliveryMode' && value === 'PULL') {
        next.pullProtocol = prev.pullProtocol || (prev.protocol === 'rist' ? 'RIST' : 'SRT');
        next.videoCodec = 'copy';
        next.audioCodec = 'copy';
        next.videoBitrate = 'copy';
        next.audioBitrate = 'copy';
        next.resolution = 'copy';
        next.aspectRatio = 'copy';
        next.frameRate = 'copy';
        next.keyframeInterval = 'copy';
        next.bitrateMode = 'copy';
        next.audioSampleRate = 'copy';
        next.audioChannels = 'copy';
        next.deinterlace = 'none';
        next.loudnorm = false;
        next.logoOverlayEnabled = false;
        next.pullLabel = next.pullLabel || next.platformDistributor || 'Pull';
      }
      if (name === 'videoCodec' && value !== 'libx264') {
        next.transcodeProfile = '';
        next.transcodeLevel = '';
      }
      if (name === 'bitrateMode' && value === 'crf') {
        next.bitrateMode = 'cbr';
      }
      if (name === 'pullProtocol') {
        next.pullProtocol = value === 'RIST' ? 'RIST' : 'SRT';
      }
      if (name === 'protocol' && prev.deliveryMode === 'PULL') {
        next.protocol = value === 'rist' ? 'rist' : 'srt';
        next.pullProtocol = value === 'rist' ? 'RIST' : 'SRT';
      }
      return next;
    });
  };

  const handleChange = (e: any) => {
    const { name, value, checked, type } = e.target;
    updateFormValue(name, type === 'checkbox' ? checked : value);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const preset = platformOptions.find(p => p.id === form.platformDistributor);
      let finalDest = '';

      const deliveryMode = canSwitchDeliveryMode ? form.deliveryMode : 'PUSH';

      if (deliveryMode === 'PULL') finalDest = form.destination || '';
      else if (!isSocial && preset?.id === 'custom') finalDest = form.streamKey.trim();
      else finalDest = `${preset?.urlHint.replace(/\/$/, '')}/${form.streamKey.trim()}`;

      const pullProtocol = form.pullProtocol || (form.protocol === 'rist' ? 'RIST' : 'SRT');
      const protocol = isSocial ? 'rtmp' : deliveryMode === 'PULL' ? form.protocol : form.protocol;
      const payload = {
        ...form,
        protocol,
        destination: finalDest,
        platformDistributor: deliveryMode === 'PULL' ? form.pullLabel.trim() : preset?.name || 'Custom',
        ...(!initialData && isManagedService ? { isManagedService: true } : {}),
        isSocialStream: isSocial,
        deliveryMode,
        pullLabel: form.pullLabel?.trim(),
        pullProtocol,
        pullBufferMs: deliveryMode === 'PULL' && pullProtocol === 'RIST' ? Number(form.pullBufferMs || 250) : form.pullBufferMs,
        pullLatencyMs: deliveryMode === 'PULL' && pullProtocol === 'SRT' ? Number(form.pullLatencyMs || 250000) : form.pullLatencyMs,
        videoCodec: canTranscode ? form.videoCodec : 'copy',
        audioCodec: canTranscode ? form.audioCodec : 'copy',
        videoBitrate: canTranscode ? form.videoBitrate : 'copy',
        audioBitrate: canTranscode ? form.audioBitrate : 'copy',
        resolution: canTranscode ? form.resolution : 'copy',
        aspectRatio: canTranscode ? form.aspectRatio : 'copy',
        frameRate: canTranscode ? form.frameRate : 'copy',
        keyframeInterval: canTranscode ? form.keyframeInterval : 'copy',
        bitrateMode: canTranscode ? form.bitrateMode : 'copy',
        crfValue: null,
        transcodePreset: canTranscode ? form.transcodePreset : null,
        transcodeProfile: canTranscode && form.videoCodec === 'libx264' ? form.transcodeProfile : null,
        transcodeLevel: canTranscode && form.videoCodec === 'libx264' ? form.transcodeLevel : null,
        audioSampleRate: canTranscode ? form.audioSampleRate : 'copy',
        audioChannels: canTranscode ? form.audioChannels : 'copy',
        deinterlace: canTranscode ? form.deinterlace : 'none',
        scaleFilter: canTranscode ? form.scaleFilter : null,
        loudnorm: canTranscode ? form.loudnorm : false,
        loudnormTarget: canTranscode ? form.loudnormTarget : null,
        logoOverlayEnabled: canTranscode ? form.logoOverlayEnabled === true : false,
        logoOverlayPosition: canTranscode ? form.logoOverlayPosition : null,
        logoOverlayOpacity: canTranscode ? Number(form.logoOverlayOpacity ?? 100) : null,
      };

      if (deliveryMode !== 'PULL') {
        delete (payload as any).pullBufferMs;
        delete (payload as any).pullLatencyMs;
      } else if (pullProtocol === 'RIST') {
        delete (payload as any).pullLatencyMs;
      } else {
        delete (payload as any).pullBufferMs;
      }

      delete (payload as any).streamKey;

      await onSave(payload);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCustomPush = !isPullMode && !isSocial && form.platformDistributor === 'custom';
  const destinationInputLabel = isCustomPush ? 'Full URL' : 'Stream Key';
  const pullProtocolValue = form.pullProtocol === 'RIST' ? 'RIST' : 'SRT';
  const pullUrlPreview = isPullMode
    ? String(form.destination || (pullProtocolValue === 'RIST' ? 'rist://generated-after-save' : 'srt://generated-after-save'))
    : '';
  const canSubmit = isPullMode
    ? !!String(form.pullLabel || '').trim()
    : !!String(form.streamKey || '').trim();
  const modeLabel = isSocial ? 'Social Stream' : isManagedService ? 'Managed Service' : isManaged ? 'Remapped Output' : isPullMode ? 'Pull Link' : 'Direct Push';
  const protocolLabel = isPullMode ? pullProtocolValue : String(form.protocol || 'rtmp').toUpperCase();

  return (
    <ConsoleDialogFrame
      open={open}
      onClose={onClose}
      maxWidth={isManaged ? 'md' : 'sm'}
      fullWidth
      icon={<OutputIcon />}
      title={initialData ? 'Edit Output' : isSocial ? 'New Social Stream' : isManagedService ? 'New Managed Service' : isManaged ? 'New Remapped Output' : 'New Direct Output'}
      subtitle={`${modeLabel} - ${protocolLabel}`}
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          borderRadius: 4,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 24px 60px rgba(0, 0, 0, 0.55)'
            : `0 24px 60px ${alpha(theme.palette.common.black, 0.16)}`,
        },
      }}
      actions={(
        <>
          <Button onClick={onClose} disabled={isSubmitting} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={handleSubmit} disabled={isSubmitting || !canSubmit} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 2.5 }}>
            {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Save Output'}
          </Button>
        </>
      )}
    >
      <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 1.25,
            }}
          >
            {[
              ['Mode', modeLabel],
              ['Protocol', protocolLabel],
              ['Processing', canTranscode ? 'Remap' : 'Pass'],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={(theme) => ({
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2.5,
                  bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.025 : 0.018),
                  px: 1.5,
                  py: 1,
                })}
              >
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 600, color: 'text.secondary', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }} noWrap>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          {isManagedService && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Managed Service uses purchased capacity and includes full remote tech support and endpoint management.
            </Alert>
          )}

          {canSwitchDeliveryMode && (
            <SectionPanel title="Delivery Mode" icon={<SettingsEthernetIcon fontSize="small" />} subdued>
              <Stack spacing={1.5}>
                <SegmentedControl
                  value={form.deliveryMode}
                  options={[{ value: 'PUSH', label: 'Push Type' }, { value: 'PULL', label: 'Pull Type' }]}
                  onChange={(value) => updateFormValue('deliveryMode', value)}
                />

              </Stack>
            </SectionPanel>
          )}

          {!isPullMode ? (
            <SectionPanel title="Endpoint" icon={<OutputIcon fontSize="small" />}>
              <Stack spacing={1.5}>
                <ControlPair
                  left={(
                    <FormControl fullWidth size="small">
                      <InputLabel>Platform</InputLabel>
                      <Select name="platformDistributor" value={form.platformDistributor} label="Platform" onChange={handleChange}>
                        {platformOptions.map(p => (
                          <MenuItem key={p.id} value={p.id}>
                            <Box display="flex" gap={1} alignItems="center"><p.icon sx={{ color: p.color, fontSize: 18 }} /> {p.name}</Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  right={<TextField label="Name / Description" name="regionLocation" value={form.regionLocation} onChange={handleChange} size="small" fullWidth />}
                />
                <Box>
                  <FieldLabel>Protocol</FieldLabel>
                  <SegmentedControl
                    value={isSocial ? 'rtmp' : form.protocol}
                    options={isSocial ? [{ value: 'rtmp', label: 'RTMP' }] : [
                      { value: 'rtmp', label: 'RTMP' },
                      { value: 'srt', label: 'SRT' },
                      { value: 'rist', label: 'RIST' },
                    ]}
                    onChange={(value) => updateFormValue('protocol', value)}
                    disabled={isSocial}
                  />
                </Box>
                <TextField
                  label={destinationInputLabel}
                  name="streamKey"
                  value={form.streamKey}
                  onChange={handleChange}
                  type="text"
                  size="small"
                  fullWidth
                />
              </Stack>
            </SectionPanel>
          ) : (
            <SectionPanel title="Pull Link" icon={<OutputIcon fontSize="small" />}>
              <Stack spacing={1.5}>
                <Box>
                  <FieldLabel>Protocol</FieldLabel>
                  <SegmentedControl
                    value={pullProtocolValue}
                    options={[{ value: 'SRT', label: 'SRT' }, { value: 'RIST', label: 'RIST' }]}
                    onChange={(value) => updateFormValue('pullProtocol', value)}
                  />
                </Box>
                <TextField
                  label={pullProtocolValue === 'RIST' ? 'Generated RIST URL' : 'Generated SRT URL'}
                  value={pullUrlPreview}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  name="pullLabel"
                  label="Name / Description"
                  value={form.pullLabel}
                  onChange={handleChange}
                  required
                  error={!String(form.pullLabel || '').trim()}
                  helperText={!String(form.pullLabel || '').trim() ? 'Name / description is required.' : ''}
                  fullWidth
                  size="small"
                />
                {form.pullProtocol === 'RIST' ? (
                  <TextField
                    name="pullBufferMs"
                    label="Buffer ms"
                    type="number"
                    value={form.pullBufferMs}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                  />
                ) : (
                  <TextField
                    name="pullLatencyMs"
                    type="number"
                    value={form.pullLatencyMs}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                  />
                )}
              </Stack>
            </SectionPanel>
          )}

          {!isSocial && !isPullMode && (form.protocol === 'srt' || form.protocol === 'rist') && (
            <SectionPanel title="Security" icon={<ShieldIcon fontSize="small" />} subdued>
              <Stack spacing={2}>
                {form.protocol === 'srt' && (
                  <ControlPair
                    left={<TextField name="streamId" label="Stream ID" value={form.streamId} onChange={handleChange} fullWidth size="small" />}
                    right={<TextField name="passphrase" label="Passphrase" value={form.passphrase} onChange={handleChange} fullWidth size="small" />}
                  />
                )}
                {form.protocol === 'rist' && <TextField name="secret" label="Secret" value={form.secret} onChange={handleChange} fullWidth size="small" />}
              </Stack>
            </SectionPanel>
          )}

          <AdvancedOptions
            formData={form}
            handleInputChange={handleChange}
            canTranscode={canTranscode}
            isManagedMode={isManaged}
            isSocialMode={isSocial || isPullMode}
          />
        </Stack>
    </ConsoleDialogFrame>
  );
};
